import {
  PageContext,
  PageContextVariable,
  PageContextEntity,
  PageContextEntityField,
  PageContextFunction,
  PageContextPage,
} from './forge.types';
import { enumerateConditionTargets, enumerateTargetFields } from './condition-targets';
import { AtlasMap } from './atlas-map-reconcile.util';
import { declaredTypeOf, isEmptyValue } from './variable-types.util';
import { isGeometry } from '../../../../shared/geometry/geometry.util';
import { Entity } from '../../../../model/entity.model';
import { resolvedShapeOf } from '../../../../shared/entity/resolved-entity-list.util';

interface BuildPageContextParams {
  localVariable: Record<string, any>;
  globalVariable: Record<string, any>;
  newEntityTransformedListForSelected: any[];
  localEntityList: any[];
  globalEntityList: any[];
  entityListReferenceEntity: any[];
  newEntityListForSelected: any[];
  functionList: any[];
  pageList: any[];
  /** The page's Atlas map (`page.atlasVariableMap`) — the declared type of each local variable. Optional:
   *  without it local types are inferred from their values, as they always were. */
  localMap?: AtlasMap;
  /** The global store's Atlas map (`globalVariable.atlasMeta.map`) — same role, for globals. */
  globalMap?: AtlasMap;
  /** Every entity of the project. The condition paths and sibling targets follow references through it; without
   *  it they are resolved from `newEntityListForSelected` alone. */
  entities?: readonly Entity[];
}

export function buildPageContext(params: BuildPageContextParams): PageContext {
  const localVariables = extractVariables(params.localVariable, 'localVariable$', 'local_variable%', 'local.', params.localMap, 'local');
  const globalVariables = extractVariables(params.globalVariable, 'globalVariable$', 'global_variable%', 'global.', params.globalMap, 'global');
  const entities = extractEntities(params.newEntityListForSelected, params.entities);
  const entityListReferences = extractEntityRefs(params.entityListReferenceEntity);

  // Add friendly aobPath to entity list references by cross-referencing entity variables
  addAobPaths(entityListReferences, localVariables, globalVariables, entities);

  return {
    localVariables,
    globalVariables,
    entities,
    entityListReferences,
    availableFunctions: extractFunctions(params.functionList),
    // AP retired 2026-09-03. The field stays so PageContext keeps its shape and the
    // .length-guarded Workflows sections in the Forge/Atlas prompts simply render nothing.
    availableWorkflows: [],
    pages: extractPages(params.pageList),
  };
}

/**
 * A variable's type is its DECLARATION (`map`, via `declaredTypeOf`); the value is read only when nothing
 * is declared, by the same rules as always. An empty variable with a declaration is included — the AI must
 * see it, or it invents a new variable under a name that is taken. With no `map` (older callers) this is
 * exactly the old value inference.
 */
export function extractVariables(
  variables: Record<string, any>,
  prefix: string,
  entityFieldPrefix: string,
  pathPrefix: string,
  map?: AtlasMap,
  scope?: 'local' | 'global',
): PageContextVariable[] {
  const result: PageContextVariable[] = [];

  for (const [name, value] of Object.entries(variables || {})) {
    const declared = map && scope ? declaredTypeOf(map, scope, name, value) : '';
    if (declared) {
      result.push(declaredVariable(name, value, declared, prefix, entityFieldPrefix, pathPrefix));
      continue;
    }
    let type = '';
    let varName = name;
    let entityFields: PageContextEntityField[] | undefined;
    let holdsEntity: string | undefined;
    let clientSideOnly: boolean | undefined;
    // Path: what the AI sees (no encoding, just human-readable dotted names)
    let varPath = pathPrefix + name;

    if (typeof value === 'string') {
      type = isTimestamp(value) ? 'TimeStamp' : 'String';
    } else if (typeof value === 'number') {
      type = 'Number';
    } else if (typeof value === 'boolean') {
      type = 'Boolean';
    } else if (typeof value === 'object' && value !== null) {
      if (value.type === 'ENTITY') {
        type = 'ENTITY_VARIABLE';
        holdsEntity = value.entityName || value.entityId;
        clientSideOnly = true;
        if (value.data) {
          entityFields = Object.entries(value.data).map(([fname, fval]) => ({
            name: fname,
            type: inferRecordFieldType(fval),
            path: varPath + '.' + fname,
            encodedId: entityFieldPrefix + name + '$' + fname,
          }));
        }
      } else if (value.type === 'ARRAY_OF_OBJECT') {
        type = 'ENTITY_LIST_VARIABLE';
        varName = name + '.data';
        // Path stays as the original name (no .data suffix — cleaner for AI)
        varPath = pathPrefix + name;
        holdsEntity = value.entityName || value.entityId;
        clientSideOnly = true;
      }
    }

    if (type !== '') {
      result.push({
        name: varName,
        type,
        path: varPath,
        encodedId: prefix + varName,
        entityFields,
        holdsEntity,
        clientSideOnly,
      });
    }
  }

  return result;
}

/**
 * One declared variable, in the shape the inference branch below produces for the same type. The entity
 * extras (holder, fields) come from the value when it holds a record of that kind; an empty or
 * differently-shaped value contributes none — never a read off a null.
 */
function declaredVariable(
  name: string, value: any, type: string, prefix: string, entityFieldPrefix: string, pathPrefix: string,
): PageContextVariable {
  const varPath = pathPrefix + name;
  const record = !isEmptyValue(value) && typeof value === 'object' ? value : null;
  let varName = name;
  let entityFields: PageContextEntityField[] | undefined;
  let holdsEntity: string | undefined;
  let clientSideOnly: boolean | undefined;
  if (type === 'ENTITY_VARIABLE') {
    clientSideOnly = true;
    if (record?.type === 'ENTITY') {
      holdsEntity = record.entityName || record.entityId;
      if (record.data) {
        entityFields = Object.entries(record.data).map(([fname, fval]) => ({
          name: fname,
          type: inferRecordFieldType(fval),
          path: varPath + '.' + fname,
          encodedId: entityFieldPrefix + name + '$' + fname,
        }));
      }
    }
  } else if (type === 'ENTITY_LIST_VARIABLE') {
    varName = name + '.data';
    clientSideOnly = true;
    if (record?.type === 'ARRAY_OF_OBJECT') holdsEntity = record.entityName || record.entityId;
  }
  return { name: varName, type, path: varPath, encodedId: prefix + varName, entityFields, holdsEntity, clientSideOnly };
}

function extractEntityRefs(entityList: any[]): PageContextEntity[] {
  return (entityList || []).map((entity) => ({
    id: entity.id,
    name: entity.name,
    fields: extractEntityFields(entity),
    // extendedAttributes travels with the AOB reference so the AOB guard can tell an AAOB (runtime
    // key-value columns — touch only via set_key_value_in_AOB_reference / aaob_aggregate_by_label) from a
    // plain AOB. See forge-entity-ref-guard.isGeneralAobBlockTargetingAaob.
    extendedAttributes: entity.extendedAttributes === true,
    // conditionFields/siblingTargets intentionally omitted — only relevant for top-level QUERY entities
  }));
}

function extractEntities(entityList: any[], allEntities?: readonly Entity[]): PageContextEntity[] {
  return (entityList || []).map((entity) => {
    // The condition walks read the resolved shape: references followed through the live entities, never a copy.
    const resolved = Array.isArray(entity.data) ? { ...entity, data: resolvedShapeOf(entity.id, allEntities, entityList) } : entity;
    return {
    id: entity.id,
    name: entity.name,
    // status travels with the entity so Forge can tell a synced (queryable) entity from a
    // LOCAL one (created in the editor, never synced → no DB table). Forge must never emit a
    // QUERY/AGGREGATE/CREATE/UPDATE against a LOCAL entity (it fails at runtime with
    // "quering the LOCAL Entity which is not possible"). See forge.validator/forge.prompts.
    status: entity.status,
    fields: extractEntityFields(entity),
    conditionFields: extractConditionFields(resolved),
    siblingTargets: extractSiblingTargets(resolved),
  };
  });
}

/** Phase 3.4: all filterable field paths for QUERY conditions (own scalars + ref/AOB drill + AAOB). */
function extractConditionFields(entity: any): { path: string; type: string }[] {
  if (!Array.isArray(entity.data)) return [];
  return enumerateTargetFields(entity.data).map((f) => ({
    path: f.source === 'aaob' ? (f.aaobField as string) : f.path.join('.'),
    type: f.source === 'aaob' ? 'AAOB' : f.dataType,
  }));
}

/** Phase 3.4: nested AOB node paths usable as a sibling `on` target (trim nested rows). */
function extractSiblingTargets(entity: any): string[] {
  if (!Array.isArray(entity.data)) return [];
  return enumerateConditionTargets([entity]).filter((t) => t.path.includes('.')).map((t) => t.path);
}

function extractEntityFields(entity: any): PageContextEntityField[] {
  const fields: PageContextEntityField[] = [];
  const hasPercent = entity.id.includes('%');

  if (Array.isArray(entity.data)) {
    for (const field of entity.data) {
      const type = mapEntityFieldType(field.type);
      if (type) {
        const sep = hasPercent ? '$' : '$$';
        fields.push({
          name: field.name,
          type,
          path: field.name,
          encodedId: entity.id + sep + field.name,
        });
      }
    }
  } else if (typeof entity.data === 'object' && entity.data !== null) {
    for (const [name, value] of Object.entries(entity.data)) {
      const type = inferPrimitiveType(value);
      if (type) {
        const sep = hasPercent ? '$' : '$$';
        fields.push({
          name,
          type,
          path: name,
          encodedId: entity.id + sep + name,
        });
      }
    }
  }

  return fields;
}

function extractFunctions(functionList: any[]): PageContextFunction[] {
  return (functionList || []).map((f: any) => ({
    name: f.name,
    inputs: (f.inputs || []).map((i: any) => ({ name: i.name, type: i.type })),
    outputs: (f.outputs || []).map((o: any) => ({ name: o.name, type: o.type })),
    type: f.type || '',
  }));
}

function extractPages(pageList: any[]): PageContextPage[] {
  return (pageList || []).map((p: any) => ({
    id: p.id,
    name: p.name,
  }));
}

function inferPrimitiveType(value: any): string {
  if (typeof value === 'string') return isTimestamp(value) ? 'TimeStamp' : 'String';
  if (typeof value === 'number') return 'Number';
  if (typeof value === 'boolean') return 'Boolean';
  return '';
}

/**
 * A field of a RECORD variable (`local_variable%<var>$<field>`), typed the way the variable registry types
 * the same token (variable-registry.service, the local/global entity-list lanes) — so the compiler, the
 * pickers and the workspace agree on it.
 *
 * ⚠ THIS WAS `inferPrimitiveType`, WHICH RETURNS '' FOR A LIST (2026-09-28). A record's `lines` reached the
 * page context untyped, so `steps-to-blockly` created its variable with type '' and the list getter's
 * FieldVariable (ENTITY_LIST_VARIABLE only) rejected it without an error: the slot stayed NAN, the delete
 * generated no code, and the button saved the record unchanged. The Forge card's Swap Variable filters by
 * the same type, so it never offered the list either.
 * .scratch/forge-nested-list-delete/issues/01-delete-from-record-list-is-dropped.md (olympus-functions).
 */
function inferRecordFieldType(value: any): string {
  const primitive = inferPrimitiveType(value);
  if (primitive) return primitive;
  // Same AAOB test as PageService.isAaobValue: runtime label rows carry `systemKey`.
  if (Array.isArray(value)) return value[0] && typeof value[0] === 'object' && 'systemKey' in value[0] ? 'AAOB_FIELD' : 'ENTITY_LIST_VARIABLE';
  if (isGeometry(value)) return 'GEOMETRY'; // before the object test — GeoJSON is an object too
  if (value && typeof value === 'object') return 'ENTITY_VARIABLE'; // a reference to another record
  return '';
}

function mapEntityFieldType(fieldType: string): string {
  switch (fieldType) {
    case 'STRING':
    case 'UUID':
      return 'String';
    case 'NUMBER':
    case 'AUTO_INCREMENT':
      return 'Number';
    case 'BOOLEAN':
      return 'Boolean';
    case 'TIMESTAMP':
      return 'TimeStamp';
    case 'GEOMETRY':
      // default returns '' — an empty Blockly type is a socket that accepts anything.
      return 'GEOMETRY';
    default:
      return '';
  }
}

function isTimestamp(value: any): boolean {
  if (typeof value !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value) && !isNaN(Date.parse(value));
}

/**
 * Build entity-based aobPath for entity list references (AOBs).
 *
 * AOBs are entity-level definitions, NOT variable-level. `AOB:Budget_OTI1Nj%transaction`
 * means "the Budget entity has an AOB field called transaction". The path is therefore
 * `Budget.transaction` (entityName.aobField), independent of which variable holds a Budget instance.
 */
function addAobPaths(
  entityListRefs: PageContextEntity[],
  localVariables: PageContextVariable[],
  globalVariables: PageContextVariable[],
  entities: PageContextEntity[],
): void {
  for (const ref of entityListRefs) {
    const percentIdx = ref.id.indexOf('%');
    if (percentIdx === -1) continue;

    const rawEntityPart = ref.id.startsWith('AOB:') ? ref.id.slice(4, percentIdx) : ref.id.slice(0, percentIdx);
    const aobFieldName = ref.id.slice(percentIdx + 1);

    const parentEntity = entities.find(e => e.id === rawEntityPart);
    const parentEntityName = parentEntity?.name || rawEntityPart;

    // Entity-based path: EntityName.aobField (NOT variable-based)
    ref.aobPath = parentEntityName + '.' + aobFieldName;

    for (const f of ref.fields) {
      f.path = ref.aobPath + '.' + f.name;
    }
  }
}
